import { Directive, computed, input } from '@angular/core';

type ButtonVariant = 'primary' | 'secondary' | 'warning' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md';

const BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold transition-colors cursor-pointer ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:opacity-50 disabled:cursor-not-allowed';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary-600 text-white shadow-sm hover:bg-primary-700',
  secondary:
    'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 ' +
    'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800',
  warning: 'bg-yellow-400 text-gray-900 shadow-sm hover:bg-yellow-500',
  danger:
    'border border-red-200 bg-white text-red-600 hover:bg-red-50 ' +
    'dark:border-red-900 dark:bg-gray-900 dark:text-red-400 dark:hover:bg-red-950',
  ghost: 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
};

/**
 * Botón del sistema. Convención de variantes, para que las pantallas se vean
 * iguales entre sí:
 * - `primary`: la acción de la pantalla (Guardar, Aprobar, Crear) y el
 *   Buscar de las barras de filtros.
 * - `secondary` (por defecto): acciones de apoyo (Cancelar, Volver).
 * - `ghost`: acciones menores junto a una primaria, como el Limpiar de los
 *   filtros; se ve como texto, sin competir con el Buscar.
 * - `warning` / `danger`: avisos y acciones destructivas (Rechazar, Eliminar).
 */
@Directive({
  selector: 'button[uiButton], a[uiButton]',
  host: { '[class]': 'classes()' },
})
export class UiButton {
  readonly variant = input<ButtonVariant>('secondary');
  readonly size = input<ButtonSize>('md');

  protected readonly classes = computed(
    () => `${BASE} ${VARIANTS[this.variant()]} ${SIZES[this.size()]}`,
  );
}
